/**
 * A tiny, seedable pseudo-random number generator so that structures that use
 * randomness (Cuckoo eviction, reservoir sampling, MinHash coefficients) can be
 * made fully deterministic in tests and reproducible in the playground.
 */

export type RNG = () => number;

/**
 * mulberry32 — a fast 32-bit generator with good statistical properties for
 * non-cryptographic use. Returns a function producing floats in [0, 1).
 */
export function mulberry32(seed: number): RNG {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Random integer in [0, n) using the given RNG. */
export function randInt(rng: RNG, n: number): number {
  return Math.floor(rng() * n);
}

/** The default source of randomness when the caller does not supply one. */
export const defaultRng: RNG = Math.random;
